import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Ingredient } from "src/api/client-api/ingredients/useGetOneIngredient.ts";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { FoodsIcon } from "@foodpilot/foods";
import { ActivitiesFormData } from "./Activities.type";
import { isSupplierManagedBy } from "./isSupplierManagedBy";
import { getActivityDataOrganizationId } from "./getActivityDataOrganizationId";

export type ActivitiesHeaderProps = {
    ingredient: Ingredient;
    activities: ActivitiesFormData;
    toggleEditMode: boolean;
    onActionClick: () => void;
    areActivitiesSaving?: boolean;
};

export const ActivitiesHeader = (props: ActivitiesHeaderProps) => {
    const { t } = useTranslation();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const { ingredient, activities, toggleEditMode, onActionClick, areActivitiesSaving = false } = props;

    const navigate = useNavigate();
    if (selectedCompany === null) return;

    const hasOnlyTheirsActivityData = activities.suppliers.every((supplier) => {
        const currentCampaignActivityData = (supplier.ingredientSupplierActivityDatas ?? []).filter((activityData) => {
            return typeof activityData.campaign !== "string" && activityData.campaign.id === currentCampaign?.id;
        });

        if (currentCampaignActivityData.length > 1) return false;
        if (currentCampaignActivityData.length === 0) return true;

        const organizationId = getActivityDataOrganizationId(currentCampaignActivityData[0]);

        return organizationId === undefined || organizationId === selectedCompany?.id;
    });

    const managesASupplier = activities.suppliers.some((supplier) => isSupplierManagedBy(supplier, selectedCompany.id));

    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "100px" }}>
            <Box sx={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                <Typography variant="h3">{t("fournisseurs")}</Typography>
                <Typography variant="huge">-</Typography>
                <Typography variant="huge">{activities.suppliers.length}</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
                {ingredient.canModify && (
                    <Button
                        variant="ternary"
                        startIcon={<FoodsIcon size={4} icon={"checkList"} />}
                        onClick={() => {
                            const returnUrl = URLHandler.ingredient.activity(selectedCompany.id, ingredient.id);
                            const url = URLHandler.ingredient.edit(selectedCompany.id, ingredient.id, 1);

                            navigate(`${url}?returnUrl=${returnUrl}`);
                        }}
                        disabled={toggleEditMode}
                    >
                        {t("ingredient.activityform.addsuppliers")}
                    </Button>
                )}
                {managesASupplier && hasOnlyTheirsActivityData && (
                    <Button
                        variant="primary"
                        startIcon={
                            areActivitiesSaving ? <CircularProgress size={20} sx={{ marginRight: 0.5 }} />
                            : toggleEditMode ?
                                <FoodsIcon size={3} icon={"check"} />
                            :   <FoodsIcon size={2} icon={"edit"} />
                        }
                        onClick={() => onActionClick()}
                        disabled={areActivitiesSaving}
                    >
                        {toggleEditMode ?
                            t("ingredient.activityform.validatemodifications")
                        :   t("ingredient.activityform.modifydata")}
                    </Button>
                )}
            </Box>
        </Box>
    );
};
